import { useState, useEffect, useRef, useCallback } from 'react';
import { apiClient } from '../contexts/AuthContext';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Camera, Sparkles, X, Droplet, Star, Plus, Minus, Utensils, Trash2, History, ChevronDown, ChevronUp, Calendar, Trophy
} from 'lucide-react';
import { toast } from 'sonner';
import { MealTemplates, SaveDayAsTemplate } from '../components/nutrition/MealTemplates';
import { NutritionBalance } from '../components/nutrition/NutritionBalance';

// Helper: get today's date in Tahiti timezone (UTC-10)
function getTahitiDate() {
  return new Date(Date.now() - 10 * 3600000).toISOString().slice(0, 10);
}

export default function NutritionPage() {
  const [meals, setMeals] = useState([]);
  const [totals, setTotals] = useState({ calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('repas');

  // Water tracking
  const [waterData, setWaterData] = useState({ entries: [], total_ml: 0, glasses: 0 });

  // History
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [expandedDay, setExpandedDay] = useState(null);

  // AI analyzer state
  const [analyzing, setAnalyzing] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState('');
  const [mediaType, setMediaType] = useState('image/jpeg');
  const [description, setDescription] = useState('');
  const [mealDate, setMealDate] = useState(getTahitiDate());
  const [analysisResult, setAnalysisResult] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const fetchMeals = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/meals/today');
      setMeals(data.meals || []);
      setTotals(data.totals || { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 });
    } catch (err) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWater = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/water/today');
      setWaterData(data);
    } catch (err) { /* water fetch silent */ }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/nutrition/history?days=60');
      setHistory(data.history || []);
    } catch (err) { /* history fetch silent */ }
  }, []);

  useEffect(() => { fetchMeals(); fetchWater(); }, [fetchMeals, fetchWater]);

  // Delete meal
  const deleteMeal = async (mealId) => {
    try {
      await apiClient.delete(`/meals/${mealId}`);
      toast.success('Repas supprime');
      fetchMeals();
      if (showHistory) fetchHistory();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Water functions
  const addWater = async () => {
    try {
      await apiClient.post('/water', { amount_ml: 250 });
      fetchWater();
    } catch { toast.error('Erreur'); }
  };

  const removeWater = async () => {
    if (waterData.entries.length === 0) return;
    const lastEntry = waterData.entries[waterData.entries.length - 1];
    try {
      await apiClient.delete(`/water/${lastEntry.id}`);
      fetchWater();
    } catch { toast.error('Erreur'); }
  };

  // AI analyzer — compress image for Android compatibility
  const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width, h = img.height;
        if (w > maxWidth) { h = (h * maxWidth) / w; w = maxWidth; }
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(dataUrl);
      };
      img.onerror = () => resolve(null);
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Accept any image type including HEIC/HEIF from iOS
    if (!file.type.startsWith('image/') && !file.name.match(/\.(heic|heif|jpg|jpeg|png|webp)$/i)) {
      toast.error('Selectionnez une image');
      return;
    }
    try {
      // Compress for better Android/mobile compatibility
      const compressed = await compressImage(file);
      if (compressed) {
        setImagePreview(compressed);
        setImageBase64(compressed.split(',')[1]);
        setMediaType('image/jpeg');
      } else {
        // Fallback to raw FileReader
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
          setImageBase64(reader.result.split(',')[1]);
          setMediaType(file.type || 'image/jpeg');
        };
        reader.readAsDataURL(file);
      }
    } catch {
      toast.error('Erreur de chargement de l\'image');
    }
  };

  const analyzeImage = async () => {
    if (!description.trim()) { toast.error('Decris ton repas (ex: "200g de poulet grille")'); return; }
    setAnalyzing(true);
    try {
      const payload = { description, media_type: mediaType };
      if (imageBase64) payload.image_base64 = imageBase64;
      const { data } = await apiClient.post('/meals/analyze', payload);
      setAnalysisResult(data);
      toast.success('Analyse terminee !');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur analyse');
    } finally {
      setAnalyzing(false);
    }
  };

  const saveAnalyzedMeal = async () => {
    if (!analysisResult) return;
    try {
      const ts = mealDate + 'T12:00:00Z';
      await apiClient.post('/meals', {
        meal_name: description.slice(0, 50),
        calories: analysisResult.calories,
        protein: analysisResult.proteines,
        carbs: analysisResult.glucides,
        fat: analysisResult.lipides,
        fiber: analysisResult.fibres || 0,
        quality_score: analysisResult.score_qualite,
        foods: analysisResult.aliments || [],
        timestamp: ts
      });
      toast.success('Repas enregistre !');
      setImagePreview(null); setImageBase64(''); setDescription(''); setAnalysisResult(null);
      setMealDate(getTahitiDate());
      fetchMeals();
      if (showHistory) fetchHistory();
    } catch {
      toast.error('Erreur enregistrement');
    }
  };

  const toggleHistory = () => {
    if (!showHistory) fetchHistory();
    setShowHistory(!showHistory);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F0F0F] pb-20">
      <header className="bg-[#0F0F0F] px-5 pt-6 pb-2">
        <p className="text-xs text-zinc-400 font-semibold tracking-widest uppercase">Module</p>
        <h1 className="text-3xl text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Nutrition & Calories</h1>
      </header>

      {/* Tabs */}
      <div className="px-5 mb-4">
        <div className="flex gap-1 bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-1" data-testid="nutrition-tabs">
          <button
            onClick={() => setActiveTab('repas')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-sm text-sm font-medium transition-colors ${
              activeTab === 'repas' ? 'bg-[#FF6B35] text-black' : 'text-zinc-400 hover:text-white'
            }`}
            data-testid="tab-repas"
          >
            <Utensils className="w-4 h-4" /> Mes repas
          </button>
          <button
            onClick={() => setActiveTab('equilibre')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-sm text-sm font-medium transition-colors ${
              activeTab === 'equilibre' ? 'bg-[#FF6B35] text-black' : 'text-zinc-400 hover:text-white'
            }`}
            data-testid="tab-equilibre"
          >
            <Trophy className="w-4 h-4" /> Equilibre
          </button>
        </div>
      </div>

      <main className="px-5 space-y-4 animate-fade-in">
        {/* ═══════ TAB: MES REPAS ═══════ */}
        {activeTab === 'repas' && (
          <>
            {/* Daily Totals */}
            <div className="bg-[#1B1B1B] border border-[#2A2A2E] border-t-2 border-t-[#FF6B35] rounded-sm p-4" data-testid="daily-totals">
              <h3 className="text-sm font-medium text-zinc-400 mb-3">Total du jour</h3>
              <div className="grid grid-cols-5 gap-2 text-center">
                <div><p className="font-mono text-xl font-bold text-white">{Math.round(totals.calories)}</p><p className="text-xs text-zinc-400">kcal</p></div>
                <div><p className="font-mono text-xl font-bold text-white">{Math.round(totals.protein)}</p><p className="text-xs text-zinc-400">Protéines</p></div>
                <div><p className="font-mono text-xl font-bold text-white">{Math.round(totals.carbs)}</p><p className="text-xs text-zinc-400">Glucides</p></div>
                <div><p className="font-mono text-xl font-bold text-white">{Math.round(totals.fat)}</p><p className="text-xs text-zinc-400">Lipides</p></div>
                <div><p className="font-mono text-xl font-bold text-white">{Math.round(totals.fiber)}</p><p className="text-xs text-zinc-400">fibres</p></div>
              </div>
            </div>

            {/* Water Tracking */}
            <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-4" data-testid="water-tracker">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Droplet className="w-4 h-4 text-blue-400" />
                  <h3 className="text-sm font-medium text-zinc-400">Eau du jour</h3>
                </div>
                <p className="text-xs text-zinc-500">{waterData.total_ml} ml</p>
              </div>
              <div className="flex items-center justify-between">
                <button onClick={removeWater} disabled={waterData.glasses === 0} className="w-10 h-10 rounded-full bg-[#0F0F0F] border border-zinc-700 flex items-center justify-center text-zinc-400 hover:border-red-500 hover:text-red-400 transition-colors disabled:opacity-30" data-testid="water-remove-btn">
                  <Minus className="w-4 h-4" />
                </button>
                <div className="flex-1 flex items-center justify-center gap-1 px-3">
                  {Array.from({ length: Math.min(waterData.glasses, 12) }).map((_, i) => (
                    <div key={`glass-${i}`} className="w-5 h-7 rounded-sm bg-blue-500/80 border border-blue-400/50 flex items-center justify-center">
                      <Droplet className="w-3 h-3 text-white" />
                    </div>
                  ))}
                  {waterData.glasses > 12 && <span className="text-xs text-zinc-500 ml-1">+{waterData.glasses - 12}</span>}
                  {waterData.glasses === 0 && <p className="text-xs text-zinc-500">Aucun verre</p>}
                </div>
                <button onClick={addWater} className="w-10 h-10 rounded-full bg-blue-500/20 border border-blue-500/50 flex items-center justify-center text-blue-400 hover:bg-blue-500/30 transition-colors" data-testid="water-add-btn">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <p className="text-center text-sm font-mono text-white mt-2" data-testid="water-count">
                {waterData.glasses} verre{waterData.glasses !== 1 ? 's' : ''} ({waterData.total_ml} ml)
              </p>
            </div>

            {/* Journees types — Meal Templates */}
            <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-4" data-testid="meal-templates-wrapper">
              <MealTemplates onApplied={() => { fetchMeals(); }} />
            </div>

            {/* AI Analyzer */}
            <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-4 space-y-4" data-testid="ai-analyzer">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-[#FF6B35]" />
                <h3 className="text-sm font-medium text-zinc-400">Analyse IA</h3>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Date du repas
                </Label>
                <Input type="date" value={mealDate} onChange={e => setMealDate(e.target.value)} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" data-testid="meal-date-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">Decris ton repas ou aliment *</Label>
                <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder={"Exemples :\n200g de poulet grille\n1 assiette de riz avec poisson\n2 oeufs + 1 banane\nSalade thon avocat"} className="bg-[#0F0F0F] border-[#2A2A2E] text-white min-h-[80px]" data-testid="ai-description" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs mb-1.5 flex items-center gap-1"><Camera className="w-3 h-3" /> Photo (optionnelle)</Label>
                <div className="flex gap-2 mb-2">
                  <button onClick={() => { cameraInputRef.current?.click(); }} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-[#FF6B35]/10 border border-[#FF6B35]/30 rounded-sm text-xs text-[#FF6B35] font-medium hover:bg-[#FF6B35]/20 transition-colors" data-testid="take-photo-btn">
                    <Camera className="w-4 h-4" /> Prendre une photo
                  </button>
                  <button onClick={() => { fileInputRef.current?.click(); }} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm text-xs text-zinc-400 font-medium hover:border-zinc-600 transition-colors" data-testid="pick-photo-btn">
                    <Star className="w-4 h-4" /> Galerie
                  </button>
                </div>
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileSelect} className="hidden" />
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*" onChange={handleFileSelect} className="hidden" />
                {imagePreview && (
                  <div className="relative border border-[#2A2A2E] rounded-sm p-2">
                    <img src={imagePreview} alt="Apercu" className="max-h-32 mx-auto rounded-sm" />
                    <button onClick={e => { e.stopPropagation(); setImagePreview(null); setImageBase64(''); }} className="absolute top-1 right-1 bg-black/50 rounded-full p-1"><X className="w-4 h-4 text-white" /></button>
                  </div>
                )}
              </div>
              <Button onClick={analyzeImage} disabled={analyzing || !description.trim()} className="w-full bg-[#FF6B35] text-white hover:bg-[#FF8C55]" data-testid="start-analysis">
                <Sparkles className="w-4 h-4 mr-2" />
                {analyzing ? 'Analyse en cours...' : imageBase64 ? 'Analyser photo + description' : 'Estimer les calories'}
              </Button>
              {analysisResult && (
                <div className="space-y-3 pt-3 border-t border-[#2A2A2E]" data-testid="ai-results">
                  <div className="bg-[#0F0F0F] rounded-sm p-3 text-center">
                    <p className="text-xs text-zinc-500">Calories estimees</p>
                    <p className="font-mono text-3xl font-bold text-white">{analysisResult.calories}</p>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <MacroBox label="Protéines" value={analysisResult.proteines} color="#ef4444" />
                    <MacroBox label="Glucides" value={analysisResult.glucides} color="#f59e0b" />
                    <MacroBox label="Lipides" value={analysisResult.lipides} color="#3b82f6" />
                    <MacroBox label="Fibres" value={analysisResult.fibres} color="#10b981" />
                  </div>
                  {analysisResult.conseil && (
                    <div className="bg-[#FF6B35]/10 border border-[#FF6B35]/30 rounded-sm p-3">
                      <p className="text-xs text-[#FF6B35]">{analysisResult.conseil}</p>
                    </div>
                  )}
                  <Button onClick={saveAnalyzedMeal} className="w-full bg-[#FF6B35] text-white hover:bg-[#FF8C55]" data-testid="save-ai-meal">
                    <Plus className="w-4 h-4 mr-2" /> Enregistrer ce repas ({formatDate(mealDate)})
                  </Button>
                </div>
              )}
            </div>

            {/* Today's Meals */}
            {meals.length > 0 && (
              <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm" data-testid="meals-list">
                <div className="p-4 border-b border-[#2A2A2E] flex items-center gap-2">
                  <Utensils className="w-4 h-4 text-[#FF6B35]" />
                  <h3 className="text-sm font-medium text-zinc-400">Repas du jour</h3>
                  <span className="ml-auto text-xs text-zinc-500">{meals.length} repas</span>
                </div>
                <div className="divide-y divide-zinc-800/50">
                  {meals.map((meal) => (
                    <MealRow key={meal.id} meal={meal} onDelete={deleteMeal} />
                  ))}
                </div>
              </div>
            )}

            {/* History Toggle */}
            <button onClick={toggleHistory} className="w-full flex items-center justify-center gap-2 py-3 bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm text-sm text-zinc-400 hover:text-white hover:border-zinc-700 transition-colors" data-testid="toggle-history">
              <History className="w-4 h-4" />
              Historique complet
              {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {/* Detailed History */}
            {showHistory && (
              <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm" data-testid="calorie-history">
                <div className="p-4 border-b border-[#2A2A2E]">
                  <h3 className="text-sm font-medium text-zinc-400">60 derniers jours — detail par repas</h3>
                </div>
                {history.length === 0 ? (
                  <p className="p-4 text-center text-xs text-zinc-500">Aucun historique</p>
                ) : (
                  <div className="max-h-[500px] overflow-y-auto">
                    {history.map((day) => {
                      const isToday = day.date === getTahitiDate();
                      const isExpanded = expandedDay === day.date;
                      return (
                        <div key={day.date} className={`border-b border-[#2A2A2E]/50 ${isToday ? 'bg-[#FF6B35]/5' : ''}`}>
                          <button onClick={() => setExpandedDay(isExpanded ? null : day.date)} className="w-full p-3 px-4 flex items-center gap-2 hover:bg-[#1a1a24] transition-colors text-left" data-testid={`history-day-${day.date}`}>
                            <div className="flex-1">
                              <p className="text-sm text-white font-medium">{isToday ? "Aujourd'hui" : formatDate(day.date)}</p>
                              <div className="flex gap-3 text-xs text-zinc-500 mt-0.5">
                                <span>P:{Math.round(day.protein)}g</span>
                                <span>G:{Math.round(day.carbs)}g</span>
                                <span>L:{Math.round(day.fat)}g</span>
                                <span>{day.meal_count} repas</span>
                              </div>
                            </div>
                            <p className="font-mono text-sm font-bold text-[#FF6B35]">{Math.round(day.calories)} kcal</p>
                            {isExpanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
                          </button>
                          {isExpanded && day.meals && (
                            <div className="bg-[#0F0F0F] border-t border-[#2A2A2E]/50">
                              <div className="divide-y divide-zinc-800/30">
                                {day.meals.map((meal) => (
                                  <MealRow key={meal.id} meal={meal} onDelete={deleteMeal} compact />
                                ))}
                              </div>
                              {day.meal_count > 0 && (
                                <div className="px-4 py-2 border-t border-[#2A2A2E]/50 flex justify-end">
                                  <SaveDayAsTemplate date={day.date} onSaved={fetchHistory} />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ═══════ TAB: EQUILIBRE ═══════ */}
        {activeTab === 'equilibre' && (
          <div className="bg-[#1B1B1B] border border-[#2A2A2E] border-t-2 border-t-[#FF6B35] rounded-sm p-4" data-testid="nutrition-balance-wrapper">
            <NutritionBalance onRefreshNeeded={activeTab === 'equilibre'} />
          </div>
        )}
      </main>
      <BottomNav />
    </div>
  );
}

function MealRow({ meal, onDelete, compact }) {
  return (
    <div className={`${compact ? 'px-5 py-2' : 'p-4'} hover:bg-[#1a1a24] transition-colors group`}>
      <div className="flex justify-between items-start mb-0.5">
        <p className={`text-white font-medium flex-1 ${compact ? 'text-xs' : 'text-sm'}`}>{meal.meal_name}</p>
        <p className={`font-mono text-[#FF6B35] mr-2 ${compact ? 'text-xs' : 'text-sm'}`}>{Math.round(meal.calories)} kcal</p>
        <button
          onClick={() => onDelete(meal.id)}
          className="opacity-60 md:opacity-0 md:group-hover:opacity-100 p-1 rounded-sm hover:bg-red-500/20 text-zinc-500 hover:text-red-400 transition-all"
          data-testid={`delete-meal-${meal.id}`}
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
      <div className="flex gap-3 text-xs text-zinc-500">
        <span>P:{Math.round(meal.protein)}g</span>
        <span>G:{Math.round(meal.carbs)}g</span>
        <span>L:{Math.round(meal.fat)}g</span>
        {meal.quality_score && <span className="flex items-center gap-0.5"><Star className="w-3 h-3 text-[#FF6B35]" />{meal.quality_score}/10</span>}
      </div>
    </div>
  );
}

function MacroBox({ label, value, color }) {
  return (
    <div className="bg-[#0F0F0F] p-2 rounded-sm text-center" style={{ borderTop: `2px solid ${color}` }}>
      <p className="font-mono text-sm font-bold text-white">{value || 0}g</p>
      <p className="text-xs text-zinc-400">{label}</p>
    </div>
  );
}

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
}
